import React, { useEffect, useState } from "react";
import GenericCrudPage from "../common/GenericCrudPage";
import {
  getEnrollments,
  createEnrollment,
  updateEnrollment,
  deleteEnrollment,
} from "../../../services/EnrollmentService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { getUsers } from "../../../services/UserService";
import { getClasses } from "../../../services/ClassService";
import { getAcademicYears } from "../../../services/AcademicYearService";
import { getStudentGroups } from "../../../services/StudentGroupService";
import { useTranslation } from "react-i18next";

import i18n from "../../../i18n";
const statusOptions = [
  { value: "active", get label() { return i18n.t("pages.enrollments.status.active"); } },
  { value: "suspended", get label() { return i18n.t("pages.enrollments.status.suspended"); } },
  { value: "completed", get label() { return i18n.t("pages.enrollments.status.completed"); } },
  { value: "withdrawn", get label() { return i18n.t("pages.enrollments.status.withdrawn"); } },
];

const statusBadge = {
  active: "success",
  suspended: "warning",
  completed: "info",
  withdrawn: "danger",
};

const Enrollments = () => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [studentGroups, setStudentGroups] = useState([]);

  useEffect(() => {
    getStudentProfiles({ page: 1, limit: 200 }).then((res) => setStudents(res.data?.data || [])).catch(() => {});
    getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
    getClasses({ page: 1, limit: 200 }).then((res) => setClasses(res.data?.data || [])).catch(() => {});
    getAcademicYears({ page: 1, limit: 200 }).then((res) => setAcademicYears(res.data?.data || [])).catch(() => {});
    getStudentGroups({ page: 1, limit: 200 }).then((res) => setStudentGroups(res.data?.data || [])).catch(() => {});
  }, []);

  const label = (list, id) => list.find((x) => x.id === id)?.name || "-";

  const studentLabel = (id) => {
    const profile = students.find((s) => s.id === id);
    if (!profile) return "-";
    const user = users.find((u) => u.id === profile.user_id);
    return user ? `${user.email} (${profile.matricule})` : profile.matricule;
  };

  const studentOptions = students.map((s) => {
    const user = users.find((u) => u.id === s.user_id);
    return { value: s.id, label: user ? `${user.email} (${s.matricule})` : s.matricule };
  });

  return (
    <GenericCrudPage
      permissionModule="ENROLLMENT"
      title={t("pages.enrollments.title")}
      breadcrumbLabel={t("menu.enrollments.title")}
      addLabel={t("pages.enrollments.addLabel")}
      searchPlaceholder={t("common.crud.searchPlaceholder")}
      hasActiveToggle={false}
      defaultSort={{ by: "enrolled_at", dir: "desc" }}
      api={{
        list: getEnrollments,
        create: createEnrollment,
        update: updateEnrollment,
        remove: deleteEnrollment,
      }}
      filters={[
        { key: "class_id", label: t("common.fields.allClasses"), options: classes.map((c) => ({ value: c.id, label: c.name })) },
        { key: "academic_year_id", label: t("common.fields.allYears"), options: academicYears.map((y) => ({ value: y.id, label: y.name })) },
        { key: "status", label: t("common.crud.allStatuses"), options: statusOptions },
      ]}
      columns={[
        { key: "student_id", label: t("roles.STUDENT"), render: (row) => studentLabel(row.student_id) },
        { key: "class_id", label: t("common.fields.class"), render: (row) => label(classes, row.class_id) },
        { key: "academic_year_id", label: t("common.fields.year"), render: (row) => label(academicYears, row.academic_year_id) },
        { key: "student_group_id", label: t("common.ui.groupe"), render: (row) => (row.student_group_id ? label(studentGroups, row.student_group_id) : "-") },
        {
          key: "status",
          label: t("common.crud.statusColumn"),
          render: (row) => (
            <span className={`badge badge-soft-${statusBadge[row.status] || "secondary"}`}>
              {statusOptions.find((s) => s.value === row.status)?.label || row.status}
            </span>
          ),
        },
      ]}
      fields={[
        { key: "student_id", label: t("roles.STUDENT"), type: "select", required: true, options: studentOptions },
        { key: "class_id", label: t("common.fields.class"), type: "select", required: true, options: classes.map((c) => ({ value: c.id, label: c.name })) },
        { key: "academic_year_id", label: t("common.fields.academicYear"), type: "select", required: true, options: academicYears.map((y) => ({ value: y.id, label: y.name })) },
        { key: "student_group_id", label: t("common.ui.groupeOptionnel"), type: "select", options: studentGroups.map((g) => ({ value: g.id, label: g.name })) },
        { key: "status", label: t("common.crud.statusColumn"), type: "select", options: statusOptions },
      ]}
      buildPayload={(formData) => ({
        student_id: formData.student_id,
        class_id: formData.class_id,
        academic_year_id: formData.academic_year_id,
        student_group_id: formData.student_group_id || null,
        status: formData.status || "active",
      })}
      getItemLabel={(item) => studentLabel(item.student_id)}
    />
  );
};

export default Enrollments;
